import { api } from './api';

export type ConnectionTone = 'healthy' | 'attention' | 'broken' | 'neutral';

export type ConnectionState =
  | 'not_configured'
  | 'not_connected'
  | 'connecting'
  | 'verifying'
  | 'read_only'
  | 'no_rooms'
  | 'connected'
  | 'broken';

export interface ConnectionView {
  provider: string;
  display_name: string;
  state: ConnectionState;
  tone: ConnectionTone;
  headline: string;
  next_step: string | null;
  connection_id: string | null;
  can_read: boolean;
  can_write: boolean;
  domain: string | null;
  consented_by: string | null;
  consented_at: string | null;
  granted_scopes: string[];
  rooms_discovered: number;
  last_discovery_at: string | null;
  last_verified_at: string | null;
  error_code: string | null;
  requested_access: Array<{ scope: string; reason: string }>;

  /**
   * This organization's own OAuth application. Nothing else on the card is
   * reachable until it is registered — there is no ZenSpace-owned client id.
   * The secret is never returned by anything, in any shape.
   */
  app_configured: boolean;
  app_client_id: string | null;
  app_configured_by: string | null;
  app_updated_at: string | null;
  /** Whose OAuth application a consent would use. */
  app_source: 'deployment' | 'organization' | 'none';

  /**
   * Whether this organization's ZenCore API key is stored. Without it a
   * mapping registers on the calendar side and stops, so the screen says so
   * before anyone maps anything rather than after.
   */
  zencore_key_configured: boolean;
}

/**
 * `GET /calendar/connections/summary` — one entry per organization that has a
 * live connection, for the organization picker.
 *
 * Organizations with nothing connected are ABSENT rather than present with an
 * empty list, so a missing entry and "not connected" are the same thing.
 */
export interface OrganizationConnectionSummary {
  organization_id: string;
  connections: Array<{
    provider: string;
    display_name: string;
    state: ConnectionState;
    tone: ConnectionTone;
    headline: string;
  }>;
}

/** `GET /calendar/apps` — what each provider needs, and what it has been given. */
export interface ProviderAppView {
  provider: string;
  display_name: string;
  configured: boolean;
  client_id: string | null;
  configured_by: string | null;
  updated_at: string | null;
  source: 'deployment' | 'organization' | 'none';
  /**
   * What must be registered on the customer's own OAuth client, byte-for-byte.
   * Shown to be copied: both providers compare it exactly, and a trailing
   * slash is the difference between working and a dead redirect.
   */
  redirect_uri: string;
  requested_access: Array<{ scope: string; reason: string }>;
}

export interface VerifyResult {
  can_read: boolean;
  can_write: boolean;
  room_count: number;
  error_code: string | null;
  message: string | null;
}

/**
 * The connection the rest of the app can actually use, if there is one.
 *
 * A row exists per provider whether or not it is connected, so "connected" is
 * a real id plus a completed read probe — not a row, and not a granted scope.
 * One definition, because two screens disagreeing about what is connected is
 * how someone ends up on an empty room list with no explanation.
 */
export function findLiveConnection(connections: ConnectionView[]): ConnectionView | null {
  return connections.find((c) => c.connection_id && c.can_read) ?? null;
}

export const connectionsApi = {
  list: () => api<ConnectionView[]>('/calendar/connections'),

  /**
   * Spans every organization the caller belongs to, so it deliberately sends
   * no organization header — the server reads the set from the token.
   */
  summary: () =>
    api<OrganizationConnectionSummary[]>('/calendar/connections/summary', {
      withoutOrganization: true,
    }),

  beginConsent: (provider: string) =>
    api<{ consent_url: string; expires_at: string }>(`/calendar/connect/${provider}`),

  verify: (id: string) => api<VerifyResult>(`/calendar/connections/${id}/verify`, { method: 'POST' }),

  disconnect: (id: string) => api<null>(`/calendar/connections/${id}`, { method: 'DELETE' }),
};

export const providerAppsApi = {
  list: () => api<ProviderAppView[]>('/calendar/apps'),

  save: (provider: string, clientId: string, clientSecret: string) =>
    api<ProviderAppView>(`/calendar/apps/${provider}`, {
      method: 'PUT',
      data: { client_id: clientId, client_secret: clientSecret },
    }),

  remove: (provider: string) => api<null>(`/calendar/apps/${provider}`, { method: 'DELETE' }),

  /** Proved against ZenCore before it is stored; nothing is returned but counts. */
  saveZenCoreKey: (apiKey: string) =>
    api<{ pods_visible: number; subscriptions_visible: number }>('/calendar/zencore-key', {
      method: 'PUT',
      data: { api_key: apiKey },
    }),
};
